import { ScanLine, FileSpreadsheet, BarChart3, ArrowRight, CheckCircle2, RefreshCw, Tag } from "lucide-react";
import { FeaturePageLayout } from "../FeaturePageLayout";
import { PageMeta } from "@/components/seo/PageMeta";
import { useAuthDialog } from "@/lib/auth-dialog-context";

const BENEFITS = [
  {
    icon: ScanLine,
    title: "Scan your full Meesho inventory",
    desc: "The EasyLister Chrome extension scans every product and variant in your Meesho inventory — MSP, WDRP, and status — without you opening each catalog manually.",
  },
  {
    icon: Tag,
    title: "Edit MSP, WDRP & MRP in bulk",
    desc: "Update prices for individual products or entire catalogs at once. Auto-calculate WDRP from your MSP with a fixed deduction you set.",
  },
  {
    icon: RefreshCw,
    title: "Filter by status — Active, OOS, Low Stock",
    desc: "Quickly find out-of-stock or low-stock products and update their prices or pause them before they affect your rankings.",
  },
  {
    icon: FileSpreadsheet,
    title: "Export to Excel in seconds",
    desc: "Download selected or all changed products as XLSX with every price field included. Share with your team or keep for your records.",
  },
];

const STEPS = [
  "Open the Meesho Inventory page with the EasyLister extension active",
  "Click Scan Inventory — the extension reads every product across all pages in the background",
  "Scan results appear in the Price Manager panel automatically",
  "Search, filter, and edit prices inline — apply to individual rows or full catalogs",
  "Export to Excel or submit updated prices directly",
];

export const PriceManagerLandingPage = () => {
  const { openAuth } = useAuthDialog();

  return (
    <FeaturePageLayout currentPath="/price-manager">
      <PageMeta
        title="Price Manager — Bulk MSP & WDRP Updates for Meesho Sellers"
        description="Scan your Meesho inventory, edit MSP/WDRP/MRP in bulk, and export price changes to Excel in seconds."
        keywords="meesho price update bulk, meesho MSP update, meesho WDRP update, meesho price manager, bulk price change meesho, meesho inventory price tool"
        path="/price-manager"
        ogTitle="Price Manager — Bulk MSP & WDRP for Meesho | EasyLister"
        ogDescription="Scan Meesho inventory, edit prices in bulk, auto-calculate WDRP, and export to Excel. No more opening catalogs one by one."
        twitterTitle="Meesho Bulk Price Manager — MSP, WDRP & MRP in one tool"
        twitterDescription="Scan inventory, edit prices in bulk, auto WDRP, Excel export. Built for Meesho sellers."
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-6">
              EasyLister · Price Manager
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Bulk MSP & WDRP Updates{" "}
              <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
                for Meesho Sellers
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Stop opening every catalog one by one. EasyLister's Chrome extension scans your entire
              Meesho inventory, then the Price Manager panel lets you update MSP, WDRP, and MRP for
              hundreds of products in seconds — with auto-WDRP calculation and Excel export built in.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openAuth("signup")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all text-sm"
              >
                Try Free — No Card Needed <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-10 bg-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { val: "3 fields", label: "MSP · WDRP · MRP" },
              { val: "Bulk", label: "Catalog-level updates" },
              { val: "XLSX", label: "One-click Excel export" },
              { val: "Auto", label: "WDRP calculation" },
            ].map(({ val, label }) => (
              <div key={label}>
                <div className="font-heading text-2xl font-extrabold text-white">{val}</div>
                <div className="text-purple-200 text-xs mt-1 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-12">
            Everything a Meesho seller needs for pricing
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-b from-white to-purple-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-10">
            How it works
          </h2>
          <ol className="space-y-6">
            {STEPS.map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-extrabold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-slate-700 text-base leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </FeaturePageLayout>
  );
};
